import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useConvex, useMutation } from "convex/react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";

import { api } from "../../convex/_generated/api";
import { Authenticated, AuthLoading, Unauthenticated } from "@/components/auth-gate";
import { Splash } from "@/components/shell/splash";
import { Wordmark } from "@/components/shell/wordmark";
import { roleName } from "@/components/studio-console/format";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { authClient } from "@/lib/auth-client";
import { said } from "@/lib/errors";

/** Where an invitation to a studio or a brand lands (the link in its email). */
export const Route = createFileRoute("/invite/$invitationId")({
  component: InvitePage,
});

const em = { em: <span className="italic" /> };

function InvitePage() {
  const { t: tc } = useTranslation();
  return (
    <>
      <AuthLoading>
        <Splash subtitle={tc("status.loading")} />
      </AuthLoading>
      <main className="mx-auto grid min-h-svh max-w-md content-center gap-6 px-6 py-10">
        <Wordmark size="md" />
        <Unauthenticated>
          <SignedOut />
        </Unauthenticated>
        <Authenticated>
          <Invitation />
        </Authenticated>
      </main>
      <Toaster />
    </>
  );
}

function SignedOut() {
  const { t } = useTranslation("studio");
  const { invitationId } = Route.useParams();
  const next = `/invite/${invitationId}`;
  return (
    <div className="grid gap-4">
      <h1 className="font-display text-3xl leading-tight text-ink">
        <Trans t={t} i18nKey="inviteLink.titleUnknown" components={em} />
      </h1>
      <p className="text-sm text-ink-2">{t("inviteLink.signedOut")}</p>
      <div className="flex flex-wrap gap-2">
        <Button asChild>
          <Link to="/login" search={{ next, mode: "sign-up" }}>
            {t("inviteLink.signUp")}
          </Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/login" search={{ next }}>
            {t("inviteLink.signIn")}
          </Link>
        </Button>
      </div>
    </div>
  );
}

type Details = {
  organizationId: string;
  organizationName: string;
  inviterEmail: string;
  email: string;
  role: string;
  status: string;
};

function Invitation() {
  const { t } = useTranslation("studio");
  const { t: tc } = useTranslation();
  const { invitationId } = Route.useParams();
  const convex = useConvex();
  const switchTo = useMutation(api.workspaces.switchTo);
  const navigate = useNavigate();
  const [details, setDetails] = useState<Details | null | undefined>(undefined);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    void authClient.organization.getInvitation({ query: { id: invitationId } }).then(({ data, error }) => {
      if (!live) return;
      if (error || !data) {
        const code = (error as { code?: string } | null)?.code ?? "";
        setProblem(code.includes("RECIPIENT") ? t("inviteLink.wrongEmail") : t("inviteLink.gone"));
        setDetails(null);
        return;
      }
      if (data.status !== "pending") {
        setProblem(t("inviteLink.gone"));
        setDetails(null);
        return;
      }
      setDetails(data as Details);
    });
    return () => {
      live = false;
    };
  }, [invitationId, t]);

  async function accept() {
    if (!details) return;
    setBusy(true);
    try {
      const { error } = await authClient.organization.acceptInvitation({ invitationId });
      if (error) {
        const code = (error as { code?: string }).code ?? "";
        throw new Error(code.includes("EMAIL_VERIFICATION") ? t("inviteLink.verify") : error.message || t("inviteLink.failed"));
      }
      // Where they work now: a brand's console, the studio once it's open, else its waiting page.
      const companies = await convex.query(api.organizations.mine, {});
      const joined = companies.find((c) => c.id === details.organizationId);
      if (joined?.kind === "brand") {
        await navigate({ to: "/brands" });
      } else if (joined?.workspaceId) {
        await switchTo({ workspaceId: joined.workspaceId });
        await navigate({ to: "/" });
      } else {
        await navigate({ to: "/studio/setup" });
      }
    } catch (error) {
      toast.error(said(error, t("inviteLink.failed")));
      setBusy(false);
    }
  }

  async function decline() {
    setBusy(true);
    await authClient.organization.rejectInvitation({ invitationId });
    toast.success(t("inviteLink.declined"));
    await navigate({ to: "/" });
  }

  if (details === undefined) return <p className="text-sm text-ink-3">{tc("status.loading")}</p>;
  if (details === null) {
    return (
      <div className="grid gap-4">
        <p className="text-sm text-ink-2">{problem}</p>
        <Link to="/" className="text-sm text-ink underline">
          {t("console.backHome")}
        </Link>
      </div>
    );
  }
  return (
    <div className="grid gap-4">
      <h1 className="font-display text-3xl leading-tight text-ink">
        <Trans t={t} i18nKey="inviteLink.title" values={{ name: details.organizationName }} components={em} />
      </h1>
      <p className="text-sm text-ink-2">
        {t("inviteLink.body", { inviter: details.inviterEmail, email: details.email, role: roleName(t, details.role) })}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => void accept()} disabled={busy}>
          {t(details.role === "member" ? "inviteLink.joinBrand" : "inviteLink.join")}
        </Button>
        <Button variant="outline" onClick={() => void decline()} disabled={busy}>
          {t("inviteLink.decline")}
        </Button>
      </div>
    </div>
  );
}
